/* eslint-disable */
import { useGetLng } from "@saleor/components/Shop/query";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { maybe, joinDateTime } from "@saleor/misc";
import RotaryDeleteDialog from "@saleor/rotary/components/RotaryDeleteDialog";
import RotaryItemDeleteDialog from "@saleor/rotary/components/RotaryItemDeleteDialog";
import RotaryItemEditDialog from "@saleor/rotary/components/RotaryItemEditDialog";
import RotaryPage, {
  RotaryPageFormData
} from "@saleor/rotary/components/RotaryPage";
import {
  useRotaryDeleteMutation,
  useRotaryItemsCreateMutation,
  useRotaryItemsDeleteMutation,
  useRotaryItemsUpdateMutation,
  useRotaryUpdateLanguageCodeMutation,
  useRotaryUpdateMutation
} from "@saleor/rotary/mutations";
import { useRotaryDetailsQuery } from "@saleor/rotary/queries";
import {
  rotaryListPath,
  rotaryUrl,
  RotaryUrlDialog,
  RotaryUrlQueryParams
} from "@saleor/rotary/urls";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React from "react";
import { useIntl } from "react-intl";

interface RotaryDetailsProps {
  id: string;
  params: RotaryUrlQueryParams;
}

const RotaryDetails: React.FC<RotaryDetailsProps> = ({ id, params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const [openModal, closeModal] = createDialogActionHandlers<
    RotaryUrlDialog,
    RotaryUrlQueryParams
  >(navigate, params => rotaryUrl(id, params), params);

  const { data, loading } = useRotaryDetailsQuery({
    variables: {
      id
    }
  });
  const { data: lngData } = useGetLng({});
  const availableLngs = lngData?.shop?.languages?.map(i => ({
    id: i.code,
    name: i.language
  }));
  const [rotaryDelete, rotaryDeleteOpts] = useRotaryDeleteMutation({
    onCompleted: data => {
      if (data.rotaryDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Rotary deleted"
          })
        });
        navigate(rotaryListPath);
      }
    }
  });
  const [rotaryUpdateLanguage] = useRotaryUpdateLanguageCodeMutation({
    onCompleted: data => {
      if (data.rotaryUpdateLanguageCode.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Language Code updated"
          })
        });
      }
    }
  });

  const [
    rotaryItemsDelete,
    rotaryItemsDeleteOpts
  ] = useRotaryItemsDeleteMutation({
    onCompleted: data => {
      if (data.rotaryItemsDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "item deleted",
            description: "rotary item deleted"
          })
        });
        closeModal();
      }
    }
  });

  const [
    rotaryItemsUpdate,
    rotaryItemsUpdateOpts
  ] = useRotaryItemsUpdateMutation({
    onCompleted: data => {
      if (data.rotaryItemsUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        closeModal();
      }
    }
  });

  const [rotaryUpdate, rotaryUpdateOpts] = useRotaryUpdateMutation({
    onCompleted: data => {
      if (data.rotaryUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const [
    rotaryItemsCreate,
    rotaryItemsCreateOpts
  ] = useRotaryItemsCreateMutation({
    onCompleted: data => {
      if (data.rotaryItemsCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added new item",
            description: "added new rotary item"
          })
        });
        closeModal();
      }
    }
  });
  const handleUpdate = async (data: RotaryPageFormData) => {
    const input = {
      ...data,
      startDate: joinDateTime(data.startDate, data.startTime),
      endDate: data.hasEndDateTime
        ? joinDateTime(data.endDate, data.endTime)
        : null
    };
    delete input.languageCode;
    delete input.endTime;
    delete input.startTime;
    delete input.hasEndDateTime;
    !input.startDate && delete input.startDate;
    const result = await rotaryUpdate({
      variables: {
        id,
        input
      }
    });
    return result.data.rotaryUpdate.errors;
  };

  const handleSubmit = handleUpdate;
  const [, setTextChange] = React.useState<boolean>();
  return (
    <>
      <RotaryPage
        rotary={maybe(() => data.rotary)}
        disabled={loading}
        errors={rotaryUpdateOpts.data?.rotaryUpdate.errors || []}
        onBack={() => navigate(rotaryListPath)}
        onDelete={() => openModal("remove")}
        onSubmit={handleSubmit}
        onItemsAdd={() => openModal("add-value")}
        onValueDelete={id =>
          openModal("remove-value", {
            id
          })
        }
        onValueReorder={null}
        onValueUpdate={id =>
          openModal("edit-value", {
            id
          })
        }
        saveButtonBarState={rotaryUpdateOpts.status}
        items={maybe(() => data.rotary.items)}
        availableLngs={availableLngs}
        rotaryUpdateLanguage={languageCode => {
          return rotaryUpdateLanguage({
            variables: {
              id,
              languageCode
            }
          });
        }}
      />
      <RotaryDeleteDialog
        open={params.action === "remove"}
        name={maybe(() => data.rotary.theme, "...")}
        confirmButtonState={rotaryDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          rotaryDelete({
            variables: {
              id
            }
          })
        }
      />
      <RotaryItemDeleteDialog
        rotaryName={maybe(() => data.rotary.theme, "...")}
        open={params.action === "remove-value"}
        text={maybe(
          () => data.rotary.items.find(item => params.id === item.id).text,
          "..."
        )}
        useName={true}
        confirmButtonState={rotaryItemsDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          rotaryItemsDelete({
            variables: {
              id: params.id
            }
          })
        }
      />
      <RotaryItemEditDialog
        rotaryItem={null}
        confirmButtonState={rotaryItemsCreateOpts.status}
        disabled={loading}
        errors={rotaryItemsCreateOpts.data?.rotaryItemsCreate.errors || []}
        open={params.action === "add-value"}
        onClose={closeModal}
        onSubmit={async inputdata => {
          const input = {
            fromRotary: data.rotary.id,
            probability: inputdata.probability,
            text: inputdata.text,
            voucher: inputdata.voucher?.id
          };
          const result = await rotaryItemsCreate({
            variables: {
              input
            }
          });
          return result.data.rotaryItemsCreate.errors;
        }}
        textChange={setTextChange}
      />
      <RotaryItemEditDialog
        rotaryItem={maybe(() =>
          data.rotary.items.find(value => params.id === value.id)
        )}
        confirmButtonState={rotaryItemsUpdateOpts.status}
        disabled={loading}
        errors={rotaryItemsUpdateOpts.data?.rotaryItemsUpdate.errors || []}
        open={params.action === "edit-value"}
        onClose={closeModal}
        onSubmit={inputdata => {
          const input = {
            fromRotary: data.rotary.id,
            probability: inputdata.probability,
            text: inputdata.text,
            voucher: inputdata.voucher?.id
          };
          return rotaryItemsUpdate({
            variables: {
              id: data.rotary.items.find(item => params.id === item.id).id,
              input
            }
          });
        }}
        textChange={setTextChange}
      />
    </>
  );
};

export default RotaryDetails;
